import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Navigate, useNavigate} from 'react-router-dom';

import {Spinner} from '../../Spinner';
import {ChannelLogo} from '../../channels/ChannelLogo';
import {ANALYTICS_CHANNEL} from '../../channels/channels';
import {getTranslatedName} from '../../selects/actionCategories';
import {LoadErrorState} from '../../elements/LoadErrorState';
import {ArrowLeft, Check, WarningCircle} from '../../icons';
import {
    METHOD_INCLUDE_ALL_CAMPAIGNS, METHOD_INCLUDE_CAMPAIGN_ON_CONDITION, METHOD_SELECT_CAMPAIGNS_FROM_LIST,
} from '../../business_units/accounts/AccountCampaignsConfiguration';
import {useLineWizard, wizardPath} from '../LineWizardScreen';
import {clearDraft, isMaterialized} from '../lineWizardDraft';
import {channelName} from '../accountsList';
import {
    campaignsInToday, campaignsLeftOutToday, conversionState, editPayload, selectionIsValid, selectsCampaigns,
} from '../campaignsPreview';
import {LeftOutTodayPreview} from '../../elements/LeftOutTodayPreview';
import {errorMessageForCode} from '../wizardErrors';
import * as api from '../lineWizardApi';
import {CampaignsSelectionModal} from './CampaignsSelectionModal';
import {ConversionEventsModal} from './ConversionEventsModal';
import {Ga4AccountModal} from './Ga4AccountModal';

/**
 * Paso 4 · Campañas — «qué mira cada cuenta».
 *
 * La jerarquía que pidió Pablo (29/07): **campañas y evento de conversión son lo importante** —son
 * los dos campos que la gente siempre deja sin completar, y la razón por la que el wizard existe—,
 * así que van como dos tarjetas grandes con un preview legible. GA4 a nivel cuenta es secundario
 * («ni en pedo lo hacés en todas las cuentas»): una línea discreta abajo, y sólo si la agencia tiene
 * Analytics conectado (04/08). Sin conversión elegida, la tarjeta va EN ROJO. Y no hay botón
 * «Editar»: se edita tocando la tarjeta.
 *
 * La línea ya existe (se creó al salir del paso 3). Lo de acá se guarda recién con «Finalizar».
 */
export function StepCampaigns() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {draft} = useLineWizard();
    const businessUnitId = draft.created.businessUnitId;

    const [configs, setConfigs] = useState(null);
    const [budgetInfo, setBudgetInfo] = useState(null);
    const [analyticsAccounts, setAnalyticsAccounts] = useState([]);
    const [loadError, setLoadError] = useState(null);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const load = useCallback(() => {
        if (!businessUnitId) return;
        setLoadError(null);
        setConfigs(null);
        Promise.all([
            api.fetchCampaignsConfigurations(businessUnitId),
            api.fetchBusinessUnitBudget(businessUnitId),
            api.fetchAnalyticsAccounts(businessUnitId),
        ]).then(([configsResponse, budgetResponse, analyticsResponse]) => {
            if (configsResponse?.status !== 'OK' || budgetResponse?.status !== 'OK') {
                setLoadError('line_wizard.load_error');
                return;
            }
            setBudgetInfo(budgetResponse.data || {});
            // Las propiedades de GA4 son un extra: si fallan, la línea de Analytics no se ofrece.
            setAnalyticsAccounts(analyticsResponse?.status === 'OK' ? (analyticsResponse.data || []) : []);
            setConfigs(configsResponse.data || {});
        });
    }, [businessUnitId]);

    useEffect(load, [load]);

    // Se llega acá sólo con la línea creada: sin ella no hay nada que configurar.
    if (!isMaterialized(draft)) return <Navigate to={wizardPath('cuentas')} replace/>;

    const edit = (configId, changes) => {
        setConfigs(current => ({...current, [configId]: {...current[configId], ...changes}}));
        setEditing(null);
        setSaveError(null);
    };

    const ga4Available = !!(budgetInfo && budgetInfo.ga4_is_enabled && budgetInfo.ga4_is_connected)
        && analyticsAccounts.length > 0;
    const allowedMethods = budgetInfo && budgetInfo.selection_methods_allowed;

    const finish = () => {
        if (saving) return;
        const invalid = Object.keys(configs).find(configId => !selectionIsValid(configs[configId]));
        if (invalid) {
            setSaveError(t('line_wizard.campaigns.invalid_selection', {account: configs[invalid].name,
                                                                       interpolation: {escapeValue: false}}));
            return;
        }
        setSaving(true);
        setSaveError(null);
        api.putCampaignsConfigurationsEdit(businessUnitId, editPayload(configs)).then(response => {
            setSaving(false);
            if (response?.status !== 'OK') {
                setSaveError(errorMessageForCode(t, response, 'line_wizard.error.finish'));
                return;
            }
            const clientId = draft.created.clientId || draft.client.id;
            clearDraft();
            navigate(`/client/${clientId}`);
        });
    };

    if (loadError !== null) {
        return (
            <div className="lw-step">
                <LoadErrorState title={t(loadError)} text={t('line_wizard.load_error_explanation')}
                                retryLabel={t('line_wizard.load_error_retry')} onRetry={load}/>
            </div>
        );
    }
    if (configs === null) return <div className="box-loader"><Spinner color="positive"/></div>;

    const configIds = Object.keys(configs);
    const editingConfig = editing ? configs[editing.configId] : null;
    // Una propiedad de GA4 entre las cuentas de la línea es Analytics A NIVEL LÍNEA: ahí lo que se
    // ofrece en cada cuenta ya no es «sumar Analytics» sino asignarle su propio tráfico.
    const lineHasGa4 = configIds.some(configId => configs[configId].channel === ANALYTICS_CHANNEL);

    return (
        <div className="lw-step">
            <div className="ob-head">
                <h1>{t('line_wizard.campaigns.title')}</h1>
                <p>{t('line_wizard.campaigns.subtitle')}</p>
            </div>

            <div className="lw-cards">
                {configIds.map(configId => (
                    <AccountCard key={configId} configId={configId} config={configs[configId]}
                                 ga4Available={ga4Available} analyticsAccounts={analyticsAccounts}
                                 lineHasGa4={lineHasGa4}
                                 onEdit={kind => setEditing({configId, kind})}/>
                ))}
            </div>

            {saveError && <p className="lw-error" role="alert">{saveError}</p>}

            <div className="lw-foot">
                <span className="lw-foot__spacer"/>
                <button type="button" className="ob-btn -ghost" onClick={() => navigate(wizardPath('cuentas'))}
                        disabled={saving}>
                    <ArrowLeft size={16}/> {t('line_wizard.back')}
                </button>
                <button type="button" className="ob-btn -primary" onClick={finish} disabled={saving}>
                    {saving ? <Spinner color="negative small"/> : <Check size={16} weight="bold"/>}
                    {t('line_wizard.campaigns.finish')}
                </button>
            </div>

            {editingConfig && editing.kind === 'campaigns' && (
                <CampaignsSelectionModal config={editingConfig} configId={editing.configId} allowedMethods={allowedMethods}
                                         onSave={changes => edit(editing.configId, changes)} onClose={() => setEditing(null)}/>
            )}
            {editingConfig && editing.kind === 'conversions' && (
                <ConversionEventsModal config={editingConfig}
                                       onSave={changes => edit(editing.configId, changes)} onClose={() => setEditing(null)}/>
            )}
            {editingConfig && editing.kind === 'ga4' && (
                <Ga4AccountModal config={editingConfig} analyticsAccounts={analyticsAccounts}
                                 onSave={changes => edit(editing.configId, changes)} onClose={() => setEditing(null)}/>
            )}
        </div>
    );
}

/**
 * Con Analytics asociado, la línea dice CUÁL propiedad: «asociado a esta cuenta» a secas no dejaba
 * confirmar la elección sin volver a abrir el modal (QA en producción, 27/09).
 *
 * Sin asociar, el texto depende de si la línea ya tiene GA4: con GA4 a nivel línea, «Sumar Google
 * Analytics» se leía como si faltara, cuando lo opcional es darle a esta cuenta su propio tráfico
 * (QA de fede, 02/10). Sin GA4 en la línea, sumarlo acá sí es lo que se ofrece.
 */
function ga4Label(t, config, analyticsAccounts, lineHasGa4) {
    const propertyId = config.ga4_ad_account_account_id;
    if (!propertyId) {
        return lineHasGa4 ? t('line_wizard.campaigns.ga4_optional_specific') : t('line_wizard.campaigns.ga4_optional');
    }
    const property = (analyticsAccounts || []).find(account => String(account.account_id) === String(propertyId));
    return property
        ? t('line_wizard.campaigns.ga4_associated_named', {name: property.name, interpolation: {escapeValue: false}})
        : t('line_wizard.campaigns.ga4_associated');
}

function AccountCard({config, ga4Available, analyticsAccounts, lineHasGa4, onEdit}) {
    const {t} = useTranslation();
    const conversion = conversionState(config);
    const hasCampaigns = selectsCampaigns(config.channel);
    const showGa4 = ga4Available && config.channel !== ANALYTICS_CHANNEL && hasCampaigns;

    return (
        <article className="lw-account-card">
            <header className="lw-account-card__head">
                <span className="lw-logo">
                    <ChannelLogo channel={config.channel} hspace={0} vspace={0} marginLeft={0} padding={0}
                                 width={20} height={20}/>
                </span>
                <span className="lw-account-card__name">{config.name}</span>
                <span className="lw-account-card__id">{channelName(config.channel)} · ID {config.platform_account_id}</span>
            </header>

            {!hasCampaigns && conversion === 'NOT_APPLICABLE' ? (
                <p className="lw-help">{t('line_wizard.campaigns.whole_account')}</p>
            ) : (
                <div className="lw-tiles">
                    {hasCampaigns && (
                        <button type="button" className="lw-tile" onClick={() => onEdit('campaigns')}>
                            <span className="lw-tile__label">{t('line_wizard.campaigns.tile_campaigns')}</span>
                            <CampaignsSummary config={config}/>
                        </button>
                    )}
                    {conversion !== 'NOT_APPLICABLE' && (
                        <button type="button" className={`lw-tile${conversion === 'MISSING' ? ' -alert' : ''}`}
                                onClick={() => onEdit('conversions')}>
                            <span className="lw-tile__label">{t('line_wizard.campaigns.tile_conversions')}</span>
                            <ConversionSummary config={config} state={conversion}/>
                        </button>
                    )}
                </div>
            )}

            {/* Con el logo de GA4 adelante: el renglón gris solo se perdía debajo de las dos
                tarjetas (recorrido de fede, 01/10, punto 14). */}
            {showGa4 && (
                <div className="lw-ga4-line">
                    <span className="lw-logo">
                        <ChannelLogo channel={ANALYTICS_CHANNEL} hspace={0} vspace={0} marginLeft={0} padding={0}
                                     width={20} height={20}/>
                    </span>
                    <span>{ga4Label(t, config, analyticsAccounts, lineHasGa4)}</span>
                    <button type="button" className="ob-link" onClick={() => onEdit('ga4')}>
                        {lineHasGa4 && !config.ga4_ad_account_account_id
                            ? t('line_wizard.campaigns.ga4_configure_specific')
                            : t('line_wizard.campaigns.ga4_configure')}
                    </button>
                </div>
            )}
        </article>
    );
}

function CampaignsSummary({config}) {
    const {t} = useTranslation();
    const inToday = campaignsInToday(config);
    let main;
    if (config.selection_method === METHOD_INCLUDE_CAMPAIGN_ON_CONDITION) {
        main = config.exclude_containing
            ? t('line_wizard.campaigns.summary_condition_both', {include: config.include_containing || '—',
                exclude: config.exclude_containing, interpolation: {escapeValue: false}})
            : t('line_wizard.campaigns.summary_condition', {include: config.include_containing || '—',
                interpolation: {escapeValue: false}});
    } else if (config.selection_method === METHOD_SELECT_CAMPAIGNS_FROM_LIST) {
        main = t('line_wizard.campaigns.summary_manual', {count: (config.selected_campaigns || []).length});
    } else if (config.selection_method === METHOD_INCLUDE_ALL_CAMPAIGNS) {
        main = t('line_wizard.campaigns.summary_all');
    } else {
        main = t('line_wizard.campaigns.summary_other');
    }
    return (
        <>
            <span className="lw-tile__main">{main}</span>
            <span className="lw-tile__detail">
                {inToday === null
                    ? t('line_wizard.campaigns.today_unknown')
                    : t('line_wizard.campaigns.today', {count: inToday.length})}
            </span>
            <LeftOutTodayPreview leftOut={campaignsLeftOutToday(config)} compact/>
        </>
    );
}

function ConversionSummary({config, state}) {
    const {t} = useTranslation();
    if (state === 'MISSING') {
        return (
            <span className="lw-tile__main">
                <WarningCircle size={16} weight="bold"/> {t('line_wizard.campaigns.conversions_missing')}
            </span>
        );
    }
    if (state === 'ALL_PRIMARY') {
        return <span className="lw-tile__main">{t('line_wizard.campaigns.conversions_all_primary')}</span>;
    }
    const selected = config.selected_actions || [];
    // El mismo nombre que muestra el selector del modal: el tile decía «Pixel Leads» (el crudo de la
    // plataforma) justo después de elegir «Clientes potenciales del píxel» (QA en producción, 27/09).
    const names = (config.all_actions || [])
        .filter(action => selected.includes(action.action_id))
        .map(action => getTranslatedName(action.action_id) || action.description || action.name);
    return (
        <>
            <span className="lw-tile__main">{names.length ? names.slice(0, 2).join(', ') : selected.length}</span>
            <span className="lw-tile__detail">{t('line_wizard.campaigns.conversions_count', {count: selected.length})}</span>
        </>
    );
}
